import { Box, OptionType, SelectMenu } from '@fullstory/components';
import React from 'react';
import { SessionConstraintsSelect } from '../SessionConstraintsSelect';
import { DisabledEventToggle } from '../DisabledEventToggle';
import { FormattedMessage, useIntl } from 'react-intl';
import { WithinTimeConstraint } from '../WithinTimeConstraint';
import * as protobuf from '@fullstory/proto/google/protobuf';
import { useFlags } from '@fullstory/react-hooks';

export interface SearchModelConstraints {
  inSameSession: boolean;
  inOrder: boolean;
  completeSessions: boolean;
  withinTime: protobuf.duration;
}

export interface EventTogglesProps
  extends SearchModelConstraints,
    Pick<
      React.ComponentProps<typeof SessionConstraintsSelect>,
      'inSameSessionDisabled' | 'displayName'
    > {
  onChange: (c: SearchModelConstraints) => void;
  inOrderDisabled?: boolean;
  inOrderDisabledText?: string; // tooltip text to display if inOrder is disabled
  withinTimeDisabled: boolean;
}

// Event constraint toggles / options.
export const EventToggles: React.FC<EventTogglesProps> = ({
  inSameSession,
  inOrder,
  completeSessions,
  withinTime,
  onChange,
  inOrderDisabled,
  inOrderDisabledText,
  inSameSessionDisabled,
  withinTimeDisabled,
  displayName,
}) => {
  const flags = useFlags();
  const withinAvailable = flags.featureFlags().AppWithinTimeConstraint;

  const intl = useIntl();
  const IN_THIS_ORDER_LABEL = intl.formatMessage({ defaultMessage: 'in this order', id: 'Nm+xJ9' });
  const IN_ANY_ORDER_LABEL = intl.formatMessage({ defaultMessage: 'in any order', id: '3x8Mnr' });

  const orderOptions: OptionType<boolean>[] = [
    { label: IN_ANY_ORDER_LABEL, value: false, key: IN_ANY_ORDER_LABEL },
    { label: IN_THIS_ORDER_LABEL, value: true, key: IN_THIS_ORDER_LABEL },
  ];

  return (
    <>
      <Box marginRight={8}>
        {inOrderDisabled ? (
          <DisabledEventToggle
            label={inOrder ? IN_THIS_ORDER_LABEL : IN_ANY_ORDER_LABEL}
            tooltipText={
              inOrderDisabledText ??
              intl.formatMessage({ defaultMessage: 'Event ordering toggle disabled', id: '2FoFt8' })
            }
          />
        ) : (
          <SelectMenu
            onChange={opt =>
              onChange({
                inSameSession,
                completeSessions,
                withinTime,
                inOrder: opt?.value ?? false,
              })
            }
            options={orderOptions}
            selected={orderOptions.find(o => o.value === inOrder)}
            compact
            variant="underline-builder"
            size="x-small"
            testId="in-event-order"
          />
        )}
      </Box>
      <FormattedMessage
        defaultMessage="and"
        id="9stp06"
        description="Used to indicate that two settings will be applied (e.g setting x and setting y)"
      />
      <Box marginLeft={8}>
        <SessionConstraintsSelect
          inSameSession={inSameSession}
          completeSessions={completeSessions}
          onChange={c => onChange({ inOrder, withinTime, ...c })}
          inSameSessionDisabled={inSameSessionDisabled}
          displayName={displayName}
        />
      </Box>
      {withinAvailable && (
        <Box marginLeft={8}>
          <WithinTimeConstraint
            context="constraint"
            withinTime={withinTime}
            onChange={c =>
              onChange({
                inOrder,
                inSameSession,
                completeSessions,
                withinTime: c,
              })
            }
            disabled={withinTimeDisabled}
          />
        </Box>
      )}
    </>
  );
};

EventToggles.displayName = 'EventToggles';
